import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ClipLoader } from 'react-spinners';
import { notify } from '../../utils/notifications';
import ConfirmDialog from './shared/ConfirmDialog';
import api from '../../api/api';
import './WorkflowList.css';

function WorkflowList() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [allFields, setAllFields] = useState([]);
  const [allFlags, setAllFlags] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newConfig, setNewConfig] = useState({ id: '', name: '', description: '' });
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [configsRes, fieldsRes, flagsRes, templatesRes] = await Promise.all([
        api.get('/admin/facility-configs'),
        api.get('/admin/extraction-fields'),
        api.get('/admin/red-flags'),
        api.get('/admin/document-templates'),
      ]);
      setConfigs(configsRes.data);
      setAllFields(fieldsRes.data);
      setAllFlags(flagsRes.data);
      setAllTemplates(templatesRes.data);
    } catch (err) {
      notify.error('Failed to load workflow configurations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) fetchData();
  }, [isAdmin, fetchData]);

  const getConfigStats = (config) => {
    const extraction = config.extraction || [];
    const groupCount = extraction.length;
    const fieldCount = extraction.reduce((sum, g) => sum + (g.field_refs?.length || 0), 0);
    const redFlags = config.red_flags || [];
    const categoryCount = redFlags.length;
    const flagCount = redFlags.reduce((sum, c) => sum + (c.flag_refs?.length || 0), 0);
    const templateCount = (config.document_refs || []).length;
    return { groupCount, fieldCount, categoryCount, flagCount, templateCount };
  };

  const getRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diff = Date.now() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `Updated ${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Updated ${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `Updated ${days}d ago`;
    const weeks = Math.floor(days / 7);
    return `Updated ${weeks}w ago`;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newConfig.id.trim() || !newConfig.name.trim()) return;

    setCreating(true);
    try {
      await api.post('/admin/facility-configs', {
        id: newConfig.id.trim(),
        name: newConfig.name.trim(),
        description: newConfig.description.trim(),
        system_instruction: 'You are analyzing medical records. Extract facts precisely as documented.',
      });
      notify.success('Workflow created');
      navigate(`/admin/workflows/${newConfig.id.trim()}`);
    } catch (err) {
      if (err.response?.status === 409) {
        notify.error('A workflow with this ID already exists');
      } else {
        notify.error('Failed to create workflow');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/facility-configs/${deleteTarget.id}`);
      notify.success(`Workflow "${deleteTarget.name}" deactivated`);
      setDeleteTarget(null);
      fetchData();
    } catch {
      notify.error('Failed to delete workflow');
    }
  };

  if (!isAdmin) {
    return <div className="wf-page"><p>Admin access required.</p></div>;
  }

  return (
    <div className="wf-page">
      <div className="wf-page-header">
        <div>
          <h1 className="wf-page-title">Facility Workflows</h1>
          <p className="wf-page-subtitle">
            Configure extraction fields, red flags, and document templates for each facility type
          </p>
        </div>
        <button className="wf-btn-create" onClick={() => setShowCreateForm(true)}>
          + New Workflow
        </button>
      </div>

      {/* Create form */}
      {showCreateForm && (
        <div className="wf-create-form">
          <form onSubmit={handleCreate}>
            <div className="wf-create-form-header">
              <h3>Create New Workflow</h3>
              <button type="button" className="wf-create-close" onClick={() => setShowCreateForm(false)}>&times;</button>
            </div>
            <div className="wf-create-fields">
              <div className="wf-create-field">
                <label>Identifier</label>
                <input
                  type="text"
                  placeholder="e.g., snf"
                  value={newConfig.id}
                  onChange={(e) => setNewConfig({ ...newConfig, id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
                  pattern="^[a-z][a-z0-9_]*$"
                  required
                />
                <span className="wf-create-hint">Lowercase letters, numbers, and underscores only</span>
              </div>
              <div className="wf-create-field">
                <label>Name</label>
                <input
                  type="text"
                  placeholder="e.g., Skilled Nursing Facility"
                  value={newConfig.name}
                  onChange={(e) => setNewConfig({ ...newConfig, name: e.target.value })}
                  required
                />
              </div>
              <div className="wf-create-field full">
                <label>Description</label>
                <textarea
                  rows="2"
                  placeholder="Brief description of this workflow..."
                  value={newConfig.description}
                  onChange={(e) => setNewConfig({ ...newConfig, description: e.target.value })}
                />
              </div>
            </div>
            <div className="wf-create-actions">
              <button type="button" className="wf-btn-secondary" onClick={() => setShowCreateForm(false)}>Cancel</button>
              <button type="submit" className="wf-btn-primary" disabled={creating}>
                {creating ? <ClipLoader size={14} color="#fff" /> : 'Create Workflow'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="wf-loading">
          <ClipLoader size={28} color="#0D9488" />
        </div>
      )}

      {/* Config list */}
      {!loading && (
        <div className="wf-list">
          {configs.length === 0 ? (
            <div className="wf-empty">
              <div className="wf-empty-icon">+</div>
              <h3>No workflows yet</h3>
              <p>Create your first facility workflow to get started.</p>
            </div>
          ) : (
            configs.filter((c) => c.active !== false).map((config) => {
              const stats = getConfigStats(config);
              return (
                <div
                  key={config.id}
                  className="wf-card"
                  onClick={() => navigate(`/admin/workflows/${config.id}`)}
                >
                  <div className="wf-card-top">
                    <div>
                      <div className="wf-card-title">{config.name}</div>
                      <span className="wf-card-id">{config.id}</span>
                    </div>
                    <div className="wf-card-right">
                      <span className={`wf-pill ${config.active !== false ? 'wf-pill-active' : 'wf-pill-inactive'}`}>
                        {config.active !== false ? 'Active' : 'Draft'}
                      </span>
                      <button
                        className="wf-card-delete"
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(config); }}
                        title="Delete workflow"
                      >
                        &times;
                      </button>
                      <div className="wf-card-arrow">&rarr;</div>
                    </div>
                  </div>

                  <div className="wf-card-stats">
                    <span className="wf-stat">
                      <strong>{stats.groupCount}</strong> groups, <strong>{stats.fieldCount}</strong> fields
                    </span>
                    <span className="wf-stat-sep">&middot;</span>
                    <span className="wf-stat">
                      <strong>{stats.categoryCount}</strong> categories, <strong>{stats.flagCount}</strong> flags
                    </span>
                    <span className="wf-stat-sep">&middot;</span>
                    <span className="wf-stat">
                      <strong>{stats.templateCount}</strong> templates
                    </span>
                  </div>

                  {config.description && (
                    <div className="wf-card-footer">
                      <span className="wf-card-description">{config.description}</span>
                      <span className="wf-card-updated">{getRelativeTime(config.updated_at)}</span>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title={`Delete "${deleteTarget?.name}"?`}
        message="This will deactivate the workflow. Existing cases using this config will be preserved."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

export default WorkflowList;
